import {
  email as emailValidator,
  required as requiredValidator,
  helpers,
} from '@vuelidate/validators'
// Deep import: the package index also loads awesome-phonenumber.
import { ssnValidOrHasBirthDate } from 'vuelidate-validators/src/ssn'
import { inputFields } from './constants'
import type { Person } from 'shared-types'

// Derives which Vuelidate validators each Drupal webform field needs.
//
// This is intentionally framework-agnostic and message-agnostic: it returns a
// plain spec per field, and each app maps that spec to actual Vuelidate rules
// with its own validators and i18n messages. That keeps the validation logic
// shared (and unit-testable) between the Nuxt (refresh) and Astro (orgsite)
// webform renderers, which historically drifted apart.

type ElementDef = Record<string, any>
type Entry = [string, ElementDef]

// Grouping types whose children are the real fields. Must match the only
// containers WebformElement.vue recurses into — validating a field the renderer
// can't reach would lock submit on an invisible, unfixable error.
const CONTAINER_TYPES = new Set(['fieldset', 'webform_flexbox'])

// Leaf field types WebformElement.vue can actually render. Kept in lockstep with
// that component's branches (and `inputFields`): only validate what the user can
// see and fix. A required field of any other type is left to server-side
// validation rather than silently blocking the client.
const RENDERABLE_LEAF_TYPES = new Set<string>([
  ...inputFields, // tel, email, textfield, date
  'textarea',
  'radios',
  'checkbox',
  'checkboxes',
  'select',
  'webform_select_other',
  'webform_radios_other',
  'managed_file',
  'webform_image_file',
  'webform_document_file',
])

export interface FieldValidationSpec {
  /**
   * Flattened field key, matching the form's value object and the `v$[key]`
   * lookup. Webform field keys are unique form-wide; `flattenObj` keys by leaf
   * name, so this alignment relies on that uniqueness.
   */
  key: string
  required: boolean
  /** `#type === 'email'` — validate address format. */
  email: boolean
  /** `agent_ssn` / `receiver_ssn` — Swedish personal-id validator. */
  ssn: boolean
  /** `agent_mobile` — Swedish mobile-number validator. */
  mobile: boolean
  /** A postcode field: five digits, spaces ignored. */
  postcode?: boolean
  /** Single checkbox: required means "must be checked", not just "not empty". */
  checkbox: boolean
  /**
   * The element's `#pattern` and `#pattern_error`, when it declares one the
   * browser can evaluate. Drupal enforces `#pattern` server-side regardless, so
   * a field without this entry is not unvalidated — it just fails later, and
   * less helpfully.
   */
  pattern?: {
    source: string
    message: string
    /**
     * The pattern accepts `yyyy-mm-dd`, so the value is also checked against the
     * calendar. A regex can express the shape but not which dates exist, and
     * Drupal's cannot either — `2026-02-31` satisfies both and is not a day.
     */
    isoDate: boolean
  }
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

// Mysoft stores a Swedish postcode as five digits and nothing else. Plain `postcode`
// is a Drupal-only delivery address (klubbnytt order form), held to the same shape.
const POSTCODE_KEYS = new Set(['agent_postcode', 'receiver_postcode', 'postcode'])

/** "123 45" → "12345"; `null` when the value is not five digits. `\s` covers pasted nbsp. */
export const swedishPostcode = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const digits = value.replace(/\s/g, '')
  return /^\d{5}$/.test(digits) ? digits : null
}

/** Postcode fields a submission fills in with something that is not five digits. */
export const invalidWebformPostcodes = (values: Record<string, unknown>): string[] =>
  [...POSTCODE_KEYS].filter(key => {
    const value = values[key]
    // Not stringified: a JSON body can carry an object whose conversion throws.
    return typeof value === 'string'
      ? value.trim() !== '' && swedishPostcode(value) === null
      : value != null
  })

/** Rewrites valid postcodes in a submission to five digits; anything else is left alone. */
export const normalizeWebformPostcodes = (values: Record<string, unknown>): void => {
  for (const key of POSTCODE_KEYS) {
    const postcode = swedishPostcode(values[key])
    if (postcode) values[key] = postcode
  }
}

/**
 * Whether an ISO-shaped value is a day that exists. Non-ISO values pass: the
 * pattern rule already rejects those, and this must not report them twice.
 *
 * Round-trips through the calendar rather than range-checking the parts, so
 * February and leap years come out right. The year is set separately because
 * `Date.UTC` folds 0-99 into 1900-1999, which would otherwise reject every year
 * before 0100 as well as year 0.
 */
function isRealIsoDate(value: string): boolean {
  const parts = ISO_DATE.exec(value)
  if (!parts) return true
  const year = Number(parts[1])
  const month = Number(parts[2])
  const day = Number(parts[3])
  if (year === 0) return false
  const date = new Date(Date.UTC(2000, month - 1, day))
  date.setUTCFullYear(year)
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  )
}

/**
 * Whether a pattern is meant to accept ISO dates, so the calendar check applies.
 *
 * Accepting one date is not enough — a loose class like `[0-9-]{10}` accepts it
 * too, and calendar-checking a reference code would reject valid codes. A real
 * date pattern also requires the hyphens, so it rejects same-length strings
 * without them.
 */
const isIsoDatePattern = (compiled: RegExp): boolean =>
  compiled.test('2026-08-05') && !compiled.test('0000000000') && !compiled.test('----------')

// A conservative heuristic, not a complete ReDoS analysis: a group whose body
// repeats without an upper bound and is itself quantified — `(a+)+`, `(a{1,})+`,
// `((a)+)+` — backtracks catastrophically and can hang the tab, which would
// block submission. The outer quantifier counts even when bounded, since
// `(a+){50}` is no better. Bounded repetition *inside* the group is fine, which
// is what keeps the real date patterns (`(\d{4})`, `(0[1-9]|1[0-2])`) usable.
// Anything it drops is left to the server, so a false positive costs nothing.
const QUANTIFIER_BODY = String.raw`(?:[^()]|\([^()]*\))*`
const QUANTIFIED = String.raw`\)\s*(?:[+*]|\{)`
const NESTED_QUANTIFIER = new RegExp(
  String.raw`\(${QUANTIFIER_BODY}(?:[+*]|\{\d*,\})${QUANTIFIER_BODY}${QUANTIFIED}`,
)
// The other classic blowup: a quantified group whose branches can match the same
// text, `(a|aa)+`. Only a *quantified* alternation qualifies, which is why the
// date patterns keep working — their `(0[1-9]|1[0-2])` groups are not repeated.
const QUANTIFIED_ALTERNATION = new RegExp(
  String.raw`\(${QUANTIFIER_BODY}\|${QUANTIFIER_BODY}${QUANTIFIED}`,
)

// Drupal matches `#pattern` as `{^(?:<pattern>)$}`, so anchor it the same way —
// a substring match would accept values the server then rejects. A pattern JS
// can't run safely is dropped, never thrown: the server still enforces it.
function readPattern(key: string, def: ElementDef): FieldValidationSpec['pattern'] {
  const declared = def['#pattern']
  if (typeof declared !== 'string' || declared === '') return undefined
  if (NESTED_QUANTIFIER.test(declared) || QUANTIFIED_ALTERNATION.test(declared)) {
    console.warn(`[webform] #pattern on "${key}" can backtrack; left to the server`)
    return undefined
  }
  const source = `^(?:${declared})$`
  let compiled: RegExp
  try {
    compiled = new RegExp(source)
  } catch {
    console.warn(`[webform] #pattern on "${key}" is not a valid JS regex; left to the server`)
    return undefined
  }
  const message = typeof def['#pattern_error'] === 'string' ? def['#pattern_error'] : ''
  // Derived from the pattern, not from what the element is called.
  return { source, message, isoDate: isIsoDatePattern(compiled) }
}

/**
 * Resolves a field's Drupal `#states` against current form values, returning the
 * effective state keys this module reads — `#access` (visible/invisible) and
 * `#required` (required/optional).
 */
export type StatesResolver = (states: unknown, data: Record<string, any>) => Record<string, any>

// Fields the renderer auto-hides once an SSN lookup fills them in (keyed by the
// boolean flag the lookup returns). WebformElement and the rule builder share
// this so a hidden, server-autofilled field isn't validated as a missing required.
export const SSN_LOOKUP_FIELD_MAP: Record<string, string> = {
  agent_firstname: 'first',
  agent_lastname: 'last',
  agent_street: 'street',
  agent_postcode: 'postcode',
  agent_care_of: 'co',
}

// Derived once from the map above. A Set, not an `in` check: `'__proto__' in
// SSN_LOOKUP_FIELD_MAP` is true, so the operator would let that through as a
// "known" key.
const KNOWN_LOOKUP_KEYS = new Set(Object.keys(SSN_LOOKUP_FIELD_MAP))

/** Field keys hidden by a successful SSN lookup, given its returned flags. */
export const lookupHiddenFieldKeys = (lookupFields: Record<string, boolean>): string[] =>
  Object.entries(SSN_LOOKUP_FIELD_MAP)
    .filter(([, flag]) => lookupFields?.[flag])
    .map(([key]) => key)

/**
 * A lookup answers for one personnummer. Storing the answer next to the number
 * it was resolved for is what makes hiding those fields — and skipping their
 * required rule — safe to act on.
 */
export interface SsnLookupState {
  /** The normalised personnummer this result was resolved for. */
  ssn: string
  /** The per-field flags `canLookup` returned for it. */
  fields: Record<string, boolean>
}

/**
 * The lookup flags that apply to the personnummer currently in the form, or `{}`
 * when none do.
 *
 * The renderer hides a field, and the rule builder skips its `required`, purely
 * on the strength of a lookup. While the donor is editing their personnummer —
 * through the debounce, and while the request is in flight — the previous
 * answer no longer describes what the server will be able to fill in. Answering
 * `{}` there brings the fields back and validates them again, so the form can
 * never be submitted with a required field that is both empty and unchecked.
 * It also makes an out-of-order response harmless: a result is only ever
 * honoured for the exact number it was resolved for.
 */
export const activeLookupFields = (
  state: SsnLookupState | null | undefined,
  currentSsn: string,
): Record<string, boolean> => (state && state.ssn && state.ssn === currentSsn ? state.fields : {})

/**
 * The keys the lookup hid that the webform marks required — the fields whose
 * required rule was skipped on the strength of the lookup, and nothing else.
 *
 * The client sends these with the submission so the server can confirm its own
 * lookup actually filled them. A hidden field the form does not require is left
 * out: skipping its rule changed nothing, and demanding a value for it would
 * reject submissions that were always allowed to omit it.
 */
export function requiredLookupHiddenKeys(
  elements: Entry[] | Record<string, ElementDef>,
  values: Record<string, any>,
  getStatesForData: StatesResolver,
  hiddenKeys: ReadonlySet<string>,
): string[] {
  if (!hiddenKeys.size) return []
  // Collect without `hiddenKeys` so the fields it would have removed are still
  // described — their `required` is the thing being asked about.
  return collectWebformValidation(elements, values, getStatesForData)
    .filter(spec => spec.required && hiddenKeys.has(spec.key))
    .map(spec => spec.key)
}

/**
 * Reads the `lookupHiddenKeys` a submission carries. The list comes from the
 * browser, so it is taken only as a claim about which fields that client
 * stopped validating — never as a source of field names. Anything outside the
 * set we actually hide is dropped, which bounds what a caller can make the
 * server insist on.
 */
export const parseLookupHiddenKeys = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return []
  return [
    ...new Set(
      raw.filter((key): key is string => typeof key === 'string' && KNOWN_LOOKUP_KEYS.has(key)),
    ),
  ]
}

/**
 * Of the fields whose required rule the client skipped, the ones the server's
 * own lookup did not fill after all. A non-empty result is the case this whole
 * mechanism exists to catch: a required field that nothing validated, because
 * the client expected the server to fill it, and that nothing filled.
 */
export const unfilledLookupKeys = (keys: string[], body: Record<string, unknown>): string[] =>
  keys.filter(key => {
    const value = body[key]
    return typeof value !== 'string' || value.trim() === ''
  })

const toEntries = (elements: Entry[] | Record<string, ElementDef>): Entry[] =>
  Array.isArray(elements) ? (elements as Entry[]) : (Object.entries(elements) as Entry[])

const childEntries = (def: ElementDef): Entry[] =>
  Object.entries(def).filter(([key]) => !key.startsWith('#')) as Entry[]

/**
 * Walks a webform element tree and returns a validation spec for every field
 * that needs at least one validator and is currently accessible. Conditional
 * state (`#states`) is resolved against `values`, so a field hidden or made
 * optional by another field's value is excluded.
 */
export function collectWebformValidation(
  elements: Entry[] | Record<string, ElementDef>,
  values: Record<string, any>,
  getStatesForData: StatesResolver,
  hiddenKeys: ReadonlySet<string> = new Set(),
): FieldValidationSpec[] {
  const specs: FieldValidationSpec[] = []

  const walk = (entries: Entry[]) => {
    for (const [key, def] of entries) {
      if (!def || typeof def !== 'object') continue
      const type = def['#type'] as string | undefined

      // Resolve conditional state (#access, #required, ...) for the current values.
      const state = getStatesForData(def['#states'], values) ?? {}
      const accessible = state['#access'] !== false && def['#access'] !== false
      const required = (state['#required'] ?? def['#required']) === true

      if (type && CONTAINER_TYPES.has(type)) {
        if (accessible) walk(childEntries(def))
        continue
      }

      // Untyped node with children (defensive): treat as a container.
      if (!type) {
        const kids = childEntries(def)
        if (kids.length && accessible) walk(kids)
        continue
      }

      // Skip anything the renderer won't draw: decorative/non-input types,
      // input types without a render branch, or fields auto-hidden by SSN lookup —
      // so we never validate a field the user can't see and fix.
      if (!accessible || hiddenKeys.has(key) || !RENDERABLE_LEAF_TYPES.has(type)) continue

      const email = type === 'email'
      const ssn = key === 'agent_ssn' || key === 'receiver_ssn'
      const mobile = key === 'agent_mobile'
      const postcode = POSTCODE_KEYS.has(key)
      const checkbox = type === 'checkbox'
      const pattern = readPattern(key, def)

      if (required || email || ssn || mobile || postcode || pattern) {
        specs.push({ key, required, email, ssn, mobile, postcode, checkbox, pattern })
      }
    }
  }

  walk(toEntries(elements))
  return specs
}

/** Localised messages for the rules produced by {@link buildWebformRules}. */
export interface WebformValidationMessages {
  required: string
  email: string
  ssn: string
  mobile: string
  postcode: string
}

/**
 * Validators the caller supplies rather than this module importing them. The mobile
 * check is one: the Nuxt renderer passes `swedishMobile` (awesome-phonenumber, ~75 KB
 * gzip), while the Astro island passes a shape check and leaves the real one to its
 * submit endpoint, so the library stays out of its bundle.
 */
export interface WebformFieldValidators {
  mobile: (value: string) => boolean
}

// A single required checkbox must be ticked, not merely "non-empty". The
// checkbox components emit 1/0 (not true/false) into the model, so accept both.
const requiredChecked = (value: unknown) => value === true || value === 1

/**
 * Maps field specs to a Vuelidate `rules` object keyed by field name. Shared by
 * the Nuxt and Astro webform renderers so the two stay in lockstep; callers pass
 * their own translated messages.
 */
export function buildWebformRules(
  specs: FieldValidationSpec[],
  messages: WebformValidationMessages,
  validators: WebformFieldValidators,
): Record<string, Record<string, any>> {
  const rules: Record<string, Record<string, any>> = {}

  for (const spec of specs) {
    const fieldRules: Record<string, any> = {}
    if (spec.required) {
      fieldRules.required = helpers.withMessage(
        messages.required,
        spec.checkbox ? requiredChecked : requiredValidator,
      )
    }
    if (spec.email) {
      fieldRules.email = helpers.withMessage(messages.email, emailValidator)
    }
    if (spec.ssn) {
      fieldRules.ssn = helpers.withMessage(
        messages.ssn,
        (value: unknown, siblings: Person): boolean => {
          if (!spec.required && (!value || value === '')) return true
          return ssnValidOrHasBirthDate(value as string, siblings)
        },
      )
    }
    if (spec.mobile) {
      fieldRules.mobile = helpers.withMessage(messages.mobile, validators.mobile)
    }
    if (spec.postcode) {
      fieldRules.postcode = helpers.withMessage(
        messages.postcode,
        // Blank (spaces only, as `required` sees it) is the `required` rule's business.
        (value: unknown) =>
          !helpers.req(typeof value === 'string' ? value.trim() : value) ||
          swedishPostcode(value) !== null,
      )
    }
    // Without `#pattern_error` there is nothing to tell the submitter, and an
    // unexplained block is worse than letting the server reject it.
    if (spec.pattern?.message) {
      const regex = new RegExp(spec.pattern.source)
      const { isoDate } = spec.pattern
      fieldRules.pattern = helpers.withMessage(spec.pattern.message, (value: unknown) => {
        // An empty value is the `required` rule's business; a pattern on an
        // optional field must not turn "left blank" into an error.
        if (value === null || value === undefined || value === '') return true
        const text = String(value)
        return regex.test(text) && (!isoDate || isRealIsoDate(text))
      })
    }
    rules[spec.key] = fieldRules
  }

  return rules
}
